import { useState } from 'react';

interface PaymentCalculatorProps {
  vehiclePrice: number;
  isCompact?: boolean;
}

export const PaymentCalculator = ({ vehiclePrice, isCompact = false }: PaymentCalculatorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [downPayment, setDownPayment] = useState(0);
  const [loanTerm, setLoanTerm] = useState(60);
  const interestRate = 6.9;

  const calculateMonthlyPayment = () => {
    const principal = vehiclePrice - downPayment;
    if (principal <= 0) return 0;
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm;
    const monthlyPayment =
      (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    return monthlyPayment;
  };

  const monthlyPayment = calculateMonthlyPayment();

  if (isCompact) {
    return (
      <div className="w-full">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full py-2 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-all duration-200 flex items-center justify-between whitespace-nowrap cursor-pointer"
        >
          <span className="text-sm text-white/70">Est. Payment</span>
          <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-[#C9A84C]`}></i>
        </button>

        {isOpen && (
          <div className="mt-3 p-4 bg-black/40 border border-white/10 rounded-lg space-y-4">
            <div>
              <label className="block text-white/70 text-xs mb-1.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Down Payment
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                <input
                  type="number"
                  value={downPayment}
                  onChange={(e) => setDownPayment(Math.max(0, Number(e.target.value)))}
                  className="w-full pl-7 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#C9A84C]/50"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/70 text-xs mb-1.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Loan Term
              </label>
              <select
                value={loanTerm}
                onChange={(e) => setLoanTerm(Number(e.target.value))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#C9A84C]/50"
              >
                <option value={24}>24 months</option>
                <option value={36}>36 months</option>
                <option value={48}>48 months</option>
                <option value={60}>60 months</option>
                <option value={72}>72 months</option>
              </select>
            </div>

            <div className="pt-3 border-t border-white/10">
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-white/60">Monthly Payment</span>
                <span className="text-2xl font-bold text-[#C9A84C]">
                  ${monthlyPayment.toFixed(0)}<span className="text-sm">/mo</span>
                </span>
              </div>
              <p className="text-white/60 text-xs text-center mt-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Based on {interestRate}% APR · For estimation purposes only
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full bg-black/40 border border-white/10 rounded-lg overflow-hidden">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-all duration-200"
      >
        <div className="flex items-center gap-2">
          <i className="ri-calculator-line text-[#C9A84C] text-lg"></i>
          <span className="text-base font-semibold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Payment Calculator
          </span>
        </div>
        <div className="flex items-center gap-3">
          {!isOpen && (
            <span className="text-sm text-[#C9A84C] font-semibold">
              ~${monthlyPayment.toFixed(0)}/mo
            </span>
          )}
          <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-[#C9A84C] text-lg transition-transform duration-300`}></i>
        </div>
      </button>

      {/* Collapsible Body */}
      {isOpen && (
        <div className="px-6 pb-6 border-t border-white/10 pt-4 space-y-4">
          <p className="text-white/70 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Estimate your monthly payment with our calculator
          </p>

          <div>
            <label className="block text-white/70 text-xs mb-1.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Down Payment
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-lg">$</span>
              <input
                type="number"
                value={downPayment}
                onChange={(e) => setDownPayment(Math.max(0, Number(e.target.value)))}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#C9A84C]/50"
                placeholder="0"
              />
            </div>
          </div>

          <div>
            <label className="block text-white/70 text-xs mb-1.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Loan Term
            </label>
            <select
              value={loanTerm}
              onChange={(e) => setLoanTerm(Number(e.target.value))}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#C9A84C]/50"
            >
              <option value={24}>24 months</option>
              <option value={36}>36 months</option>
              <option value={48}>48 months</option>
              <option value={60}>60 months</option>
              <option value={72}>72 months</option>
            </select>
          </div>

          <div className="pt-4 border-t border-white/10">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm text-white/70">Estimated Monthly Payment</span>
              <span className="text-3xl font-bold text-[#C9A84C]">
                ${monthlyPayment.toFixed(0)}<span className="text-lg">/mo</span>
              </span>
            </div>
            <p className="text-xs text-white/50">
              Based on {interestRate}% APR for {loanTerm} months · For estimation purposes only
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
